import styled, { keyframes, css } from "styled-components";
import Header from "../components/Header";
import { Fade, Slide, Zoom } from 'react-awesome-reveal';
import Footer from '../components/Footer';
import DynamicContent from '../components/DynamicContent';
import ViewerCount from '../components/ViewerCount';
import firestoreService from "../services/firestoreService";
import storageService from "../services/storageService";
import { useLocation, useParams } from "react-router-dom";
import { useEffect, useState, useRef, useCallback } from "react";

function Home() {
    const location = useLocation();
    const { pixelId } = useParams();
    const [campaignData, setCampaignData] = useState(null);
    const [campaignSetting, setCampaignSetting] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // 스크롤 추적 관련 상태
    const [scrollDocId, setScrollDocId] = useState(null);
    const scrollTimeoutRef = useRef(null);
    const lastScrollPositionRef = useRef(0);
    const [isPC, setIsPC] = useState(window.innerWidth > 1024);

    useEffect(() => {
        setLoading(true);
        getCampaignData();
        setLoading(false);
    }, [pixelId]);

    useEffect(() => {
        // 필요한 서비스들을 전역으로 노출
        window.firestoreService = firestoreService;
        window.storageService = storageService;
        
        // 도메인 기반 픽셀 번호 조회 함수
        window.getPixelNumberByDomain = async () => {
            try {
                const domain = window.location.hostname;
                
                // 도메인으로 픽셀 번호 조회
                const result = await firestoreService.getCollectionWithQuery('DBCampaign', [
                    { field: 'domain', operator: '==', value: domain }
                ]);

        
                
                if (result.success && result.data.length > 0) {
                    const pixelNumber = result.data[0].pixel_id;
                    
                    return pixelNumber;
                }
                
                return null;
            } catch (error) {
                console.error('Error fetching pixel number by domain:', error);
                return null;
            }
        };
        

        
        // 세션 ID 생성 및 관리
        if (!sessionStorage.getItem('sessionId')) {
            sessionStorage.setItem('sessionId', Date.now().toString());
        }
        
        // 컴포넌트 언마운트 시 정리
        return () => {
            delete window.firestoreService;
            delete window.storageService;
            delete window.getPixelNumberByDomain;
        };
    }, []);

    // 화면 크기 변경 감지
    useEffect(() => {
        const handleResize = () => {
            setIsPC(window.innerWidth > 1024);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 스크롤 이벤트 처리
    useEffect(() => {
        const handleScroll = () => {
            // campaignData가 로드되고 스크롤 위치가 100px 이상일 때만 저장
            if (campaignData && campaignData.id && window.scrollY >= 100) {
                // 이전 타이머가 있다면 취소
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }

                // 현재 스크롤 위치 계산
                const scrollTop = Math.max(
                    window.pageYOffset,
                    document.documentElement.scrollTop,
                    document.body.scrollTop
                );
                
                const clientHeight = Math.max(
                    document.documentElement.clientHeight,
                    window.innerHeight
                );
                
                // 화면 중앙의 절대 위치 계산
                const centerPosition = Math.round(scrollTop + (clientHeight / 2));
                
                // 스크롤이 멈춘 후 500ms 후에 저장
                scrollTimeoutRef.current = setTimeout(async () => {
                    // 현재 스크롤 위치 다시 확인
                    const currentScrollTop = Math.max(
                        window.pageYOffset,
                        document.documentElement.scrollTop,
                        document.body.scrollTop
                    );
                    const currentCenterPosition = Math.round(currentScrollTop + (clientHeight / 2));
                    
                    // 스크롤 위치가 변경되지 않았을 때만 저장
                    if (Math.abs(currentCenterPosition - centerPosition) < 10) {
                        await saveScrollPosition(currentCenterPosition, 'scroll');
                    }
                }, 500); // 스크롤이 멈춘 후 500ms 대기
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [pixelId, scrollDocId, isPC, campaignData?.id]);

    // 페이지 이탈 시 스크롤 위치 저장
    // useEffect(() => {
    //     const handleBeforeUnload = () => {
    //         if (campaignData && campaignData.id && scrollDocId && window.scrollY >= 100) {
    //             const scrollData = {
    //                 scrollPosition: Math.round(window.scrollY + (window.innerHeight / 2)),
    //                 timestamp: new Date().toISOString(),
    //                 isPC: isPC,
    //                 deviceType: isPC ? 'PC' : 'Mobile',
    //                 eventType: 'page_exit',
    //                 campaignId: pixelId,
    //                 userAgent: navigator.userAgent,
    //                 referrer: document.referrer || '',
    //                 pageUrl: window.location.href
    //             };

    //             // 동기적으로 저장 시도 (페이지 이탈 시)
    //             navigator.sendBeacon && navigator.sendBeacon(
    //                 `/api/save-scroll-exit`,
    //                 JSON.stringify(scrollData)
    //             );
    //         }
    //     };

    //     window.addEventListener('beforeunload', handleBeforeUnload);
    //     return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    // }, [campaignData, scrollDocId, isPC]);

    useEffect(() => {
        // 로고가 로드되면 HTML 문서의 title과 favicon 변경
        if (campaignData?.logo?.downloadURL) {
            // 페이지 제목 변경
            document.title = campaignData.name || '캠페인';
            
            // favicon 변경
            const favicon = document.querySelector('link[rel="icon"]') || document.querySelector('link[rel="shortcut icon"]');
            if (favicon) {
                favicon.href = campaignData.logo.downloadURL;
            } else {
                // favicon이 없으면 새로 생성
                const newFavicon = document.createElement('link');
                newFavicon.rel = 'icon';
                newFavicon.href = campaignData.logo.downloadURL;
                document.head.appendChild(newFavicon);
            }
        }
    }, [campaignData]);

    const getCampaignData = async () => {
        // 1. 도메인 기반 조회 시도
        let result = await getCampaignDataWithDomain();
        
        if(result){
            return;
        }
        
        // 2. pixelId가 있는 경우에만 pixelId 기반 조회 시도
        if(pixelId){
            result = await getCampaignDataWithPixelId();
        }
        
        // 3. 둘 다 실패한 경우
        if(!result){
            setLoading(false);
        }
    }


    const getCampaignDataWithPixelId = async () => {
        const campaign = await firestoreService.getCollectionWithQuery('DBCampaign', [{field: 'pixel_id', operator: '==', value: pixelId}]);
        if(campaign.data[0]){
            setCampaignData(campaign.data[0]);
            getCampaignSetting(campaign.data[0].id);
            return true;
        }
        return false;
    }

    const getCampaignDataWithDomain = async () => {
        const campaign = await firestoreService.getCollectionWithQuery('DBCampaign', [{field: 'domain', operator: '==', value: window.location.hostname}]);
        if(campaign.data[0]){
            setCampaignData(campaign.data[0]);
            getCampaignSetting(campaign.data[0].id);
            return true;
        }
        return false;
    }

    const getCampaignSetting = async (campaignId) => {
        const setting = await firestoreService.getDocument('DBCampaignSetting', campaignId);
        setCampaignSetting(setting.data);
    }

    // 스크롤 위치 저장 함수
    const saveScrollPosition = useCallback(async (scrollPosition, eventType = 'scroll') => {
        if (!campaignData || !campaignData.id) {
            return;
        }


        try {
            const scrollData = {
                scroll_position: scrollPosition,
                timestamp: new Date().toISOString(),
                pc: isPC,
                device_type: isPC ? 'PC' : 'Mobile',
                event_type: eventType,
                campaign_id: campaignData.id,
                user_agent: navigator.userAgent,
                referrer: document.referrer || '',
                page_url: window.location.href,
                session_id: sessionStorage.getItem('sessionId') || Date.now().toString()
            };

            if (!scrollDocId) {
                const result = await firestoreService.createDocument(`scroll/${campaignData.id}/datas`, scrollData);
                if (result.success) {
                    setScrollDocId(result.documentId);
                    sessionStorage.setItem('scrollDocId', result.documentId);
                }
            } else {
                await firestoreService.updateDocument(`scroll/${campaignData.id}/datas`, scrollDocId, scrollData);
            }
        } catch (error) {
            // 에러 발생 시 조용히 처리
        }
    }, [campaignData, isPC, pixelId, scrollDocId]);


    if(!campaignData){
        return null;
    }
    
    // campaignData가 없으면 로딩 상태 표시
    if (loading) {
        return (
            <Container>
                <Header />
                <MainContent>
                    <LoadingSection>
                        <LoadingText>로딩 중...</LoadingText>
                    </LoadingSection>
                </MainContent>
                <Footer />
            </Container>
        );
    }

    // campaignData.images 배열을 order 순서대로 정렬
    const sortedImages = campaignData.images?.sort((a, b) => a.order - b.order) || [];

    // index에 따라 다양한 애니메이션 컴포넌트 지정
    const animationComponents = [
        (children, idx) => <Fade key={idx} triggerOnce direction="up">{children}</Fade>,
        (children, idx) => <Slide key={idx} triggerOnce direction="left">{children}</Slide>,
        (children, idx) => <Slide key={idx} triggerOnce direction="right">{children}</Slide>,
        (children, idx) => <Zoom key={idx} triggerOnce>{children}</Zoom>,
    ];

    return(
        <Container>
            <Header logo={campaignData.logo} campaignSetting={campaignSetting}/>
            
            {/* 시청자 수 표시 */}
            {campaignData.viewerCountSettings && (
                <ViewerCount 
                    settings={campaignData.viewerCountSettings} 
                    campaignId={campaignData.id} 
                />
            )}
            
            {/* 특가 할인 배너 */}
            {campaignData.specialOffer && (
                <SpecialOfferBanner>
                    <SpecialOfferContent>
                        <FlameEmoji>🔥</FlameEmoji>
                        <DiscountText>
                            {campaignData.specialOffer.label || '특가 할인'} {campaignData.specialOffer.discountPercent}%
                        </DiscountText>
                        <FlameEmoji>🔥</FlameEmoji>
                    </SpecialOfferContent>
                </SpecialOfferBanner>
            )}
            
            <MainContent>
                <ImageSection>
                    {/* 캠페인 이미지들 표시 */}
                    {sortedImages.map((image, idx) => {
                        if (idx === 0) {
                            return <Image src={image.downloadURL} alt={image.name} key={image.id} />;
                        }
                        const Comp = animationComponents[(idx-1) % animationComponents.length];
                        return Comp(
                            <Image src={image.downloadURL} alt={image.name} key={image.id} />, idx
                        );
                    })}
                </ImageSection>
                <QuickConsultSection>
                    {campaignSetting && campaignData ? (
                        <DynamicContent
                            htmlCode={campaignSetting.html_code}
                            cssCode={campaignSetting.css_code}
                            javascriptCode={campaignSetting.javascript_code}
                            id={campaignSetting.id}
                            scrollDocId={scrollDocId}
                            campaignId={campaignData.id}
                            kakaoContact={campaignData.kakaoContact}
                        />
                    ) : (
                        <LoadingContent>
                            <LoadingText>콘텐츠를 불러오는 중...</LoadingText>
                        </LoadingContent>
                    )}
                </QuickConsultSection>
            </MainContent>
            <Footer footer={campaignData.footer} logo={campaignData.logo} campaignSetting={campaignSetting}/>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 100vw;
    min-height: 100vh;
`

const MainContent = styled.div`
    display: flex;
    width: 100%;
    margin-top: 72px;
    
    @media (max-width: 768px) {
        flex-direction: column;
    }
    
    @media (min-width: 769px) {
        flex-direction: row;
        min-height: 100vh;
    }
`

const ImageSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    
    @media (max-width: 768px) {
        width: 100%;
        margin-top: 0;
    }
    
    @media (min-width: 769px) {
        width: 50%;
        margin-top: 0;
    }
`

const QuickConsultSection = styled.div`
    @media (max-width: 768px) {
        width: 100%;
    }
    
    @media (min-width: 769px) {
        width: 50%;
        position: fixed;
        top: 72px;
        right: 0;
        height: calc(100vh - 72px);
        display: flex;
        align-items: center;
        justify-content: center;
        background: #f8fafc;
        border-left: 1px solid #e2e8f0;
        z-index: 10;
    }
`

const Image = styled.img`
    width: 100vw;
    object-fit: cover;
    
    @media (min-width: 769px) {
        width: 100%;
        max-width: 100%;
    }
`;

const LoadingSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 50vh;
`

const LoadingText = styled.div`
    font-size: 18px;
    color: #666;
`

const LoadingContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 200px;
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
`

const LogoContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
    padding: 20px;
`

const LogoImage = styled.img`
    max-width: 200px;
    max-height: 80px;
    object-fit: contain;
`

// 애니메이션 키프레임 정의
const slideDown = keyframes`
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const neonPulse = keyframes`
    0%, 100% {
        transform: scale(1);
        box-shadow: 
            0 0 8px rgba(240, 120, 120, 0.3),
            0 0 15px rgba(240, 120, 120, 0.2),
            0 2px 10px rgba(240, 120, 120, 0.2);
    }
    50% {
        transform: scale(1.03);
        box-shadow: 
            0 0 15px rgba(240, 120, 120, 0.5),
            0 0 25px rgba(240, 120, 120, 0.3),
            0 3px 15px rgba(240, 120, 120, 0.3);
    }
`;

const neonGlow = keyframes`
    0%, 100% {
        filter: drop-shadow(0 0 3px rgba(255, 100, 0, 0.4)) 
                drop-shadow(0 0 6px rgba(255, 150, 0, 0.3));
        transform: scale(1);
    }
    50% {
        filter: drop-shadow(0 0 6px rgba(255, 100, 0, 0.7)) 
                drop-shadow(0 0 12px rgba(255, 150, 0, 0.5));
        transform: scale(1.05);
    }
`;

const textNeon = keyframes`
    0%, 100% {
        text-shadow: 
            0 0 3px rgba(255, 255, 255, 0.3),
            0 0 6px rgba(255, 255, 255, 0.2);
    }
    50% {
        text-shadow: 
            0 0 6px rgba(255, 255, 255, 0.5),
            0 0 12px rgba(255, 255, 255, 0.3);
    }
`;

const SpecialOfferBanner = styled.div`
    position: fixed;
    top: 72px;
    display: flex;
    align-items: center;
    padding: 16px 20px;
    z-index: 1000;
    animation: ${slideDown} 0.5s ease-out;
    
    @media (max-width: 768px) {
        left: 0;
        right: 0;
        width: 100%;
        justify-content: center;
        padding: 12px 16px;
        top: 72px;
    }
    
    @media (min-width: 769px) {
        left: 0;
        width: 50%;
        justify-content: center;
    }
`

const SpecialOfferContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    background: #F07878;
    border-radius: 50px;
    padding: 14px 32px;
    animation: ${neonPulse} 2s ease-in-out infinite;
    
    @media (max-width: 768px) {
        padding: 12px 24px;
        gap: 10px;
    }
    
    &:hover {
        animation-play-state: paused;
    }
`

const DiscountText = styled.span`
    font-size: 24px;
    font-weight: 700;
    color: #FFFFFF;
    letter-spacing: 0.5px;
    text-align: center;
    white-space: nowrap;
    animation: ${textNeon} 2s ease-in-out infinite;
    
    @media (max-width: 768px) {
        font-size: 18px;
        letter-spacing: 0.3px;
    }
    
    @media (max-width: 480px) {
        font-size: 16px;
    }
`

const FlameEmoji = styled.span`
    font-size: 24px;
    line-height: 1;
    animation: ${neonGlow} 1.5s ease-in-out infinite;
    
    @media (max-width: 768px) {
        font-size: 20px;
    }
    
    &:nth-child(1) {
        animation-delay: 0s;
    }
    
    &:nth-child(3) {
        animation-delay: 0.75s;
    }
`

export default Home;